import { useState } from 'react';
import { ButtonSection, Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DocumentSelectorModal } from '@frontend/document-selector-modal';
import { useCombinedDocumentsListV3 } from '@frontend/documents-hooks';
import { useModalControl } from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import { CustomToolbar } from '../atoms/toolbars/custom-toolbar';
import { DefaultToolbar } from '../atoms/toolbars/default-toolbar';
import { OptionsToolbar } from '../atoms/toolbars/options-toolbar';
import { SelectedToolbar } from '../atoms/toolbars/selected-toolbar';
import type { ToolbarView } from '../atoms/toolbars/types';

type Props = {
  link?: Link;
};

export const ButtonToolbar = ({ link }: Props) => {
  const [view, setView] = useState<ToolbarView>(!!link?.url ? 'selected' : 'default');
  const { setProps } = useComposerSection<ButtonSection>();
  const { form, locationIds, setForm } = useContentComposer();

  const setLink = (newLink: Link) => {
    setProps((props) => (props.link = newLink));
    setView('selected');
  };

  const documents = useCombinedDocumentsListV3(locationIds);
  const { modalProps, triggerProps } = useModalControl();
  const updateDocumentId = (documentId: string) => {
    setLink({ ...link, url: documentId, linkType: 'form' });
    const documentType = documents?.find((form) => form.id === documentId)?.documentType ?? 'form';
    setForm?.({ documentId, documentType });
    modalProps.onClose();
  };

  const editLink = () => {
    if (link?.linkType === 'custom') {
      setView('custom');
    } else if (link?.linkType === 'form') {
      triggerProps.onClick();
    }
  };

  const removeLink = () => {
    setLink({ ...link, url: '', linkType: '' });
    setView('default');
  };

  const getToolbar = () => {
    switch (view) {
      case 'default':
        return <DefaultToolbar setView={setView} />;

      case 'options':
        return <OptionsToolbar link={link} openFormsModal={triggerProps.onClick} setLink={setLink} setView={setView} />;

      case 'custom':
        return <CustomToolbar link={link} setLink={setLink} setView={setView} />;

      case 'selected':
        return <SelectedToolbar documents={documents} editLink={editLink} link={link} removeLink={removeLink} />;
    }
  };

  return (
    <>
      <SectionBase.Toolbar position='bottom-center'>{getToolbar()}</SectionBase.Toolbar>
      <DocumentSelectorModal
        documentId={form?.documentId}
        documents={documents}
        modalProps={modalProps}
        selectedLocationIds={locationIds}
        updateDocumentId={updateDocumentId}
      />
    </>
  );
};
